import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Users,
  FileText,
  BarChart as BarChartIcon,
  Send,
  Target,
  Clock,
  TrendingUp,
  Activity,
  RefreshCw,
  Mail
} from "lucide-react";
import { useProject } from "@/hooks/useProject";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useRealtimeSubmissions, type RealtimeSubmission } from "@/hooks/useRealtimeSubmissions";
import { useActiveCampaignStats } from "@/hooks/useActiveCampaignStats";
import { StatsCard, StatsGrid } from "@/components/dashboard/StatsCards";
import { ActivityFeed, RealtimeIndicator } from "@/components/dashboard/ActivityFeed";
import { SyncStatus } from "@/components/dashboard/SyncStatus";
import { ActiveCampaignStats } from "@/components/dashboard/ActiveCampaignStats";

const Dashboard = () => {
  const navigate = useNavigate();
  const { projectConfig } = useProject();
  const { data: stats, isLoading: isLoadingStats } = useDashboardStats();
  const { submissions: realtimeSubmissions, isConnected, isLoading: isLoadingRealtime } = useRealtimeSubmissions({ limit: 5 });
  const { data: acStats, isLoading: isLoadingAC } = useActiveCampaignStats();

  const handleSubmissionClick = (submission: RealtimeSubmission) => {
    navigate(`/admin/submissions/${submission.jotform_submission_id}?formId=${submission.jotform_form_id}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">Panel de Control</h1>
        <div
          className="flex items-center gap-2 px-3 py-1 rounded-full text-sm"
          style={{ backgroundColor: `${projectConfig.color}20`, color: projectConfig.color }}
        >
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: projectConfig.color }}
          />
          {projectConfig.name}
        </div>
      </div>

      {/* KPIs principales */}
      <StatsGrid columns={6}>
        <StatsCard
          title="Total Envíos"
          value={stats?.submissions || 0}
          icon={Send}
          isLoading={isLoadingStats}
        />
        <StatsCard
          title="Hoy"
          value={stats?.submissionsToday || 0}
          icon={TrendingUp}
          subtext="Últimas 24 horas"
          isLoading={isLoadingStats}
        />
        <StatsCard
          title="Tasa Conversión"
          value={`${stats?.conversionRate || 0}%`}
          icon={Target}
          subtext="Submissions → Reportes"
          isLoading={isLoadingStats}
        />
        <StatsCard
          title="Tiempo Promedio"
          value={stats?.avgReportTime ? `${stats.avgReportTime}h` : 'N/A'}
          icon={Clock}
          subtext="Generación de reporte"
          isLoading={isLoadingStats}
        />
        <StatsCard
          title="Clientes"
          value={stats?.clients || 0}
          icon={Users}
          isLoading={isLoadingStats}
        />
        <StatsCard
          title="Informes"
          value={stats?.withReport || 0}
          icon={FileText}
          subtext={`${stats?.pending || 0} pendientes`}
          isLoading={isLoadingStats}
        />
      </StatsGrid>

      {/* Segunda fila: Actividad y Sincronización */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Feed de Actividad (2 columnas) */}
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Actividad Reciente
              </CardTitle>
              <RealtimeIndicator isConnected={isConnected} />
            </div>
            <CardDescription>
              Últimas respuestas recibidas en tiempo real
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ActivityFeed
              submissions={realtimeSubmissions}
              isLoading={isLoadingRealtime}
              onItemClick={handleSubmissionClick}
            />
          </CardContent>
        </Card>

        {/* Estado de Sincronización (1 columna) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Sincronización JotForm
            </CardTitle>
            <CardDescription>
              Estado de conexión con JotForm
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SyncStatus
              lastSyncAt={stats?.lastSyncAt || null}
              syncedForms={stats?.syncedForms || 0}
              isLoading={isLoadingStats}
            />
          </CardContent>
        </Card>
      </div>

      {/* Tercera fila: Active Campaign */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Active Campaign
          </CardTitle>
          <CardDescription>
            Métricas de email marketing y engagement
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ActiveCampaignStats
            stats={acStats}
            isLoading={isLoadingAC}
          />
        </CardContent>
      </Card>

      {/* Cuarta fila: Estadísticas adicionales */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatsCard
          title="Lanzamientos"
          value={stats?.launches || 0}
          icon={BarChartIcon}
          subtext="Campañas activas"
          isLoading={isLoadingStats}
        />
        <StatsCard
          title="Esta Semana"
          value={stats?.submissionsThisWeek || 0}
          icon={TrendingUp}
          subtext="Últimos 7 días"
          isLoading={isLoadingStats}
        />
      </div>
    </div>
  );
};

export default Dashboard;
